import { useState, useMemo } from "react";
import { useOutletContext, useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { get, post, patch, del } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Skeleton } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Avatar } from "@/components/common/Avatar";
import { PRIORITY_DOT, TYPE_COLOR } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { FormDialog } from "@/components/common/FormDialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchInput } from "@/components/common/SearchInput";
import { Plus, Link2, AlertTriangle, X, Paperclip } from "lucide-react";
import { toast } from "sonner";

const ITEM_TYPES = ["Epic", "Module", "Task", "Subtask", "Bug", "Feature", "Activity"];
const PRIORITIES = ["low", "medium", "high", "critical"];

export default function Board() {
  const { project } = useOutletContext();
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { can, me } = useApp();
  const [dragId, setDragId] = useState(null);
  const [shakeId, setShakeId] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [assigneeFilter, setAssigneeFilter] = useState("all");

  const { data: columns = [], isLoading: lc } = useQuery({ queryKey: ["columns", id], queryFn: () => get(`/pm/projects/${id}/columns`) });
  const { data: allItems = [], isLoading: li } = useQuery({ queryKey: ["items", id], queryFn: () => get(`/pm/projects/${id}/items`) });
  const items = useMemo(() => allItems.filter((i) => !i.in_planning), [allItems]);
  const visibleItems = useMemo(() => {
    return items.filter((i) => {
      if (search && !i.title?.toLowerCase().includes(search.toLowerCase())) return false;
      if (typeFilter !== "all" && i.type !== typeFilter) return false;
      if (priorityFilter !== "all" && i.priority !== priorityFilter) return false;
      if (assigneeFilter !== "all" && i.assignee_id !== assigneeFilter) return false;
      return true;
    });
  }, [items, search, typeFilter, priorityFilter, assigneeFilter]);
  const { data: users = [] } = useQuery({ queryKey: ["users"], queryFn: () => get("/users") });
  const umap = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);
  const { data: approvalConfig } = useQuery({ queryKey: ["approval-config", id], queryFn: () => get(`/pm/projects/${id}/approval-config`) });
  const { data: attachmentCounts = {} } = useQuery({ queryKey: ["attachment-counts", id], queryFn: () => get(`/pm/projects/${id}/attachment-counts`) });
  const byId = useMemo(() => Object.fromEntries(allItems.map((i) => [i.id, i])), [allItems]);

  const move = useMutation({
    mutationFn: ({ wid, column_id }) => patch(`/pm/items/${wid}`, { column_id, _actor: me?.id }),
    onMutate: async ({ wid, column_id }) => {
      await qc.cancelQueries({ queryKey: ["items", id] });
      const prev = qc.getQueryData(["items", id]);
      qc.setQueryData(["items", id], (old) => old.map((i) => i.id === wid ? { ...i, column_id } : i));
      return { prev };
    },
    onError: (e, v, ctx) => qc.setQueryData(["items", id], ctx.prev),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["items", id] }),
  });
  const submitApproval = useMutation({
    mutationFn: (wid) => post(`/pm/items/${wid}/submit-approval`, { _actor: me?.id }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["items", id] }); toast.success("DoD complete — submitted for L1 approval"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not submit for approval"),
  });

  const onDrop = (col) => {
    if (!dragId) return;
    const item = items.find((i) => i.id === dragId);
    const wid = dragId; setDragId(null);
    if (!item || item.column_id === col.id) return;
    // Gate: moving to Done requires all required DoD checked
    if (col.base_id === "col-done" && !item.is_spike) {
      const missing = (item.dod || []).filter((d) => d.required && !d.checked && !d.overridden);
      if (missing.length) {
        setShakeId(wid); setTimeout(() => setShakeId(null), 400);
        toast.error(`Cannot move to Done — missing: ${missing.map((m) => m.label).join(", ")}`);
        return;
      }
      const needsApproval = approvalConfig?.applies_to_types?.includes(item.type) && item.approval_status !== "approved";
      if (needsApproval) {
        submitApproval.mutate(wid);
        return;
      }
    }
    move.mutate({ wid, column_id: col.id });
    toast.success("Item moved");
  };

  if (lc || li) return <div className="flex gap-3">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-96 w-64" />)}</div>;

  const urgent = visibleItems.filter((i) => i.swimlane === "urgent");

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <div className="text-sm text-muted-foreground">Sprint 4 · {items.length} items</div>
        {can("pm", "create") && <Button size="sm" data-testid="new-item-btn" onClick={() => setNewOpen(true)}><Plus className="mr-1 h-4 w-4" />New Item</Button>}
        {can("pm", "edit") && (
          <FormDialog title="Add Column" testid="add-column-modal"
            trigger={<Button size="sm" variant="outline" data-testid="add-column-btn"><Plus className="mr-1 h-4 w-4" />Column</Button>}
            fields={[{ name: "name", label: "Column Name", required: true, full: true }, { name: "wip_limit", label: "WIP Limit (optional)", type: "number" }]}
            onSubmit={async (v) => { await post(`/pm/projects/${id}/columns`, v); qc.invalidateQueries({ queryKey: ["columns", id] }); toast.success("Column added"); }} />
        )}
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <SearchInput testid="board-search" placeholder="Search items" value={search} onChange={setSearch} />
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-40" data-testid="board-type-filter"><SelectValue placeholder="All types" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            {ITEM_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={priorityFilter} onValueChange={setPriorityFilter}>
          <SelectTrigger className="w-40" data-testid="board-priority-filter"><SelectValue placeholder="All priorities" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All priorities</SelectItem>
            {PRIORITIES.map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
          <SelectTrigger className="w-44" data-testid="board-assignee-filter"><SelectValue placeholder="All assignees" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All assignees</SelectItem>
            {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {urgent.length > 0 && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-3 dark:border-red-500/30 dark:bg-red-500/10" data-testid="urgent-swimlane">
          <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-red-600 dark:text-red-400"><AlertTriangle className="h-3.5 w-3.5" />Urgent</div>
          <div className="flex gap-2 overflow-x-auto">
            {urgent.map((it) => <Card key={it.id} it={it} umap={umap} navigate={navigate} base={`/pm/projects/${id}`} setDragId={setDragId} dragId={dragId} shakeId={shakeId} compact attachmentCounts={attachmentCounts} byId={byId} />)}
          </div>
        </div>
      )}

      <div className="flex gap-3 overflow-x-auto pb-4" data-testid="pm-board">
        {columns.map((col) => {
          const cards = visibleItems.filter((i) => i.column_id === col.id && i.swimlane !== "urgent");
          const overLimit = col.wip_limit && cards.length >= col.wip_limit;
          return (
            <div key={col.id} data-testid={`pm-col-${col.base_id}`} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(col)}
              className="flex w-72 shrink-0 flex-col rounded-xl bg-muted/40">
              <div className="flex items-center justify-between px-3 py-2.5">
                <span className="text-[13px] font-semibold">{col.name}</span>
                <div className="flex items-center gap-1.5">
                  <span className={cn("rounded-full px-2 py-0.5 text-xs", overLimit ? "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400" : "bg-muted text-muted-foreground")}>
                    {cards.length}{col.wip_limit ? `/${col.wip_limit}` : ""}
                  </span>
                  {can("pm", "edit") && cards.length === 0 && !["col-todo", "col-done"].includes(col.base_id) && (
                    <button data-testid={`del-col-${col.base_id}`} title="Delete column"
                      onClick={async () => { await del(`/pm/columns/${col.id}`); qc.invalidateQueries({ queryKey: ["columns", id] }); toast.success("Column deleted"); }}
                      className="text-muted-foreground hover:text-red-500"><X className="h-3.5 w-3.5" /></button>
                  )}
                </div>
              </div>
              <div className="flex flex-col gap-2 p-2">
                {cards.map((it) => <Card key={it.id} it={it} umap={umap} navigate={navigate} base={`/pm/projects/${id}`} setDragId={setDragId} dragId={dragId} shakeId={shakeId} attachmentCounts={attachmentCounts} byId={byId} />)}
                {cards.length === 0 && <div className="py-6 text-center text-xs text-muted-foreground">No items</div>}
              </div>
            </div>
          );
        })}
      </div>

      <NewItemModal open={newOpen} setOpen={setNewOpen} projectId={id} users={users} qc={qc} navigate={navigate} />
    </div>
  );
}

function ownerModuleId(item, byId) {
  let cur = item;
  let guard = 0;
  while (cur && cur.type !== "Module" && guard < 10) {
    cur = byId[cur.parent_id];
    guard++;
  }
  return cur?.id;
}

function Card({ it, umap, navigate, base, setDragId, dragId, shakeId, compact, attachmentCounts = {}, byId = {} }) {
  const count = attachmentCounts[it.id] || 0;
  return (
    <div data-testid={`wi-card-${it.id}`} draggable onDragStart={() => setDragId(it.id)} onDragEnd={() => setDragId(null)}
      onClick={() => navigate(`${base}/items/${it.id}`)}
      className={cn("cursor-pointer rounded-lg border bg-card p-3 transition-all hover:border-primary/40 hover:shadow-sm",
        it.blocked ? "border-red-300 dark:border-red-500/40" : "border-border",
        compact ? "w-56 shrink-0" : "", dragId === it.id && "opacity-50", shakeId === it.id && "animate-shake border-red-400")}>
      <div className="flex items-center gap-1.5">
        <StatusBadge family={TYPE_COLOR[it.type]} label={it.type} />
        {it.severity && <StatusBadge status={it.severity} />}
        {it.is_spike && <StatusBadge family="slate" label="Spike" />}
        {it.blocked && <AlertTriangle className="ml-auto h-3.5 w-3.5 text-red-500" title={it.blocked_reason || "Blocked"} />}
      </div>
      <div className="mt-2 text-[13px] font-medium leading-snug">{it.title}</div>
      {it.approval_status && (
        <StatusBadge family="amber" label={it.approval_status === "pending_l1_approval" ? "Awaiting L1" : "Awaiting L2"} />
      )}
      {it.due_date && <div className="mt-1 text-[11px] text-muted-foreground">Due {new Date(it.due_date).toLocaleDateString()}</div>}
      <div className="mt-2 flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <span className={cn("h-2 w-2 rounded-full", PRIORITY_DOT[it.priority])} />
          <span className="font-mono text-xs text-muted-foreground">{it.story_points}pt</span>
          {it.blocked_by?.length > 0 && <Link2 className="h-3 w-3 text-amber-500" />}
          <button data-testid={`card-docs-${it.id}`} title="Documents"
            onClick={(e) => { e.stopPropagation(); navigate(`${base}/documents?item=${it.id}&module=${ownerModuleId(it, byId) || ""}`); }}
            className="flex items-center gap-0.5 text-muted-foreground hover:text-foreground">
            <Paperclip className="h-3 w-3" />{count > 0 && <span className="text-[10px]">{count}</span>}
          </button>
        </div>
        <Avatar user={umap[it.assignee_id]} size="xs" />
      </div>
    </div>
  );
}

function NewItemModal({ open, setOpen, projectId, users, qc, navigate }) {
  const { me } = useApp();
  const [form, setForm] = useState({ type: "Task", priority: "medium", story_points: 3 });
  const { data: siblings = [] } = useQuery({ queryKey: ["items", projectId], queryFn: () => get(`/pm/projects/${projectId}/items`), enabled: open });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const create = useMutation({
    mutationFn: (body) => post(`/pm/projects/${projectId}/items`, body),
    onSuccess: (it) => { qc.invalidateQueries({ queryKey: ["items", projectId] }); setOpen(false); toast.success("Item created"); navigate(`/pm/projects/${projectId}/items/${it.id}`); },
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md" data-testid="new-item-modal">
        <DialogHeader><DialogTitle>New Work Item</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Type</Label>
            <Select value={form.type} onValueChange={(v) => set("type", v)}>
              <SelectTrigger className="mt-1" data-testid="ni-type"><SelectValue /></SelectTrigger>
              <SelectContent>{["Epic", "Module", "Task", "Subtask", "Bug", "Feature", "Activity"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Priority</Label>
            <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{["low", "medium", "high", "critical"].map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="col-span-2"><Label>Title *</Label><Input data-testid="ni-title" className="mt-1" onChange={(e) => set("title", e.target.value)} /></div>
          <div>
            <Label>Parent</Label>
            <Select value={form.parent_id || "none"} onValueChange={(v) => set("parent_id", v === "none" ? null : v)}>
              <SelectTrigger className="mt-1" data-testid="ni-parent"><SelectValue placeholder="No parent" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No parent</SelectItem>
                {siblings.map((s) => <SelectItem key={s.id} value={s.id}>{s.title}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Assignee</Label>
            <Select value={form.assignee_id} onValueChange={(v) => set("assignee_id", v)}>
              <SelectTrigger className="mt-1"><SelectValue placeholder="Assign" /></SelectTrigger>
              <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}{u.skills?.length ? <span className="ml-1.5 text-xs text-muted-foreground">({u.skills.slice(0, 2).join(", ")})</span> : null}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Story Points</Label><Input type="number" className="mt-1" value={form.story_points} onChange={(e) => set("story_points", Number(e.target.value))} /></div>
          <div><Label>Expected By</Label><Input type="date" className="mt-1" data-testid="ni-due" onChange={(e) => set("due_date", e.target.value ? new Date(e.target.value).toISOString() : null)} /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button data-testid="ni-create" onClick={() => { if (!form.title) return toast.error("Title required"); create.mutate({ ...form, is_bug: form.type === "Bug", assigned_by: form.assignee_id ? me?.id : null }); }}>Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
